import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Typography,
    Chip,
    Spinner,
    Card,
    CardBody
} from "@material-tailwind/react";
import {
    ClockIcon,
    UserIcon,
    DocumentTextIcon,
    ChatBubbleLeftIcon,
    CheckCircleIcon,
    ArrowPathIcon
} from "@heroicons/react/24/outline";
const { formatActivityLabel } = require('./taskActivityUtils');

const TaskActivitySection = ({ taskId }) => {
    const { rootUrl } = require('../../../config/config');
    const [token] = useState(localStorage.getItem('authToken') || '');

    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let isActive = true;

        const fetchActivities = async () => {
            setLoading(true);
            try {
                const response = await axios.get(
                    `${rootUrl}/api/tasks/${taskId}/activity`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (isActive && response.data.success) {
                    setActivities(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching activities:', error);
            } finally {
                if (isActive) {
                    setLoading(false);
                }
            }
        };

        fetchActivities();

        return () => {
            isActive = false;
        };
    }, [rootUrl, taskId, token]);

    const getActivityIcon = (activityType) => {
        const icons = {
            'created': <DocumentTextIcon className="h-5 w-5" />,
            'updated': <ArrowPathIcon className="h-5 w-5" />,
            'status_changed': <CheckCircleIcon className="h-5 w-5" />,
            'assigned': <UserIcon className="h-5 w-5" />,
            'commented': <ChatBubbleLeftIcon className="h-5 w-5" />,
            'time_logged': <ClockIcon className="h-5 w-5" />
        };
        return icons[activityType] || <DocumentTextIcon className="h-5 w-5" />;
    };

    const getActivityColor = (activityType) => {
        const colors = {
            'created': 'green',
            'updated': 'blue',
            'status_changed': 'orange',
            'assigned': 'purple',
            'commented': 'cyan',
            'time_logged': 'teal'
        };
        return colors[activityType] || 'gray';
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const parseChanges = (changesJson) => {
        if (!changesJson) return null;
        try {
            return JSON.parse(changesJson);
        } catch {
            return null;
        }
    };

    const renderActivityDescription = (activity) => {
        const changes = parseChanges(activity.changes);
        const activityType = activity.activity_type || activity.action_type || '';

        switch (activityType) {
            case 'created':
                return <Typography variant="small">created this task</Typography>;

            case 'updated':
                if (changes) {
                    return (
                        <div>
                            <Typography variant="small">updated the task:</Typography>
                            <ul className="list-disc list-inside ml-4 mt-1">
                                {Object.entries(changes).map(([field, values]) => (
                                    <li key={field} className="text-sm text-gray-700">
                                        <span className="font-semibold">{field}:</span>{' '}
                                        <span className="line-through text-gray-500">{values.old || 'none'}</span>
                                        {' → '}
                                        <span className="text-blue-600">{values.new || 'none'}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                }
                return <Typography variant="small">updated the task</Typography>;

            case 'status_changed':
                if (changes && changes.status) {
                    return (
                        <Typography variant="small">
                            changed status from{' '}
                            <Chip size="sm" value={changes.status.old} className="inline mx-1" />
                            to{' '}
                            <Chip size="sm" value={changes.status.new} className="inline mx-1" />
                        </Typography>
                    );
                }
                return <Typography variant="small">changed the status</Typography>;

            case 'assigned':
                if (changes && changes.assigned_to) {
                    return (
                        <Typography variant="small">
                            assigned this task to <strong>{changes.assigned_to.new}</strong>
                        </Typography>
                    );
                }
                return <Typography variant="small">assigned this task</Typography>;

            case 'unassigned':
                if (changes && changes.assigned_to) {
                    return (
                        <Typography variant="small">
                            removed <strong>{changes.assigned_to.old || changes.assigned_to.new || 'an assignee'}</strong>
                        </Typography>
                    );
                }
                return <Typography variant="small">removed an assignee</Typography>;

            case 'watchers_added':
                return <Typography variant="small">added watchers to the task</Typography>;

            case 'watchers_removed':
                return <Typography variant="small">removed watchers from the task</Typography>;

            case 'commented':
                return <Typography variant="small">added a comment</Typography>;

            case 'checklist_added':
                return <Typography variant="small">created a checklist</Typography>;

            case 'checklist_updated':
                return <Typography variant="small">updated a checklist</Typography>;

            case 'checklist_deleted':
                return <Typography variant="small">deleted a checklist</Typography>;

            case 'checklist_item_added':
                return <Typography variant="small">added a checklist item</Typography>;

            case 'checklist_item_completed':
                return <Typography variant="small">completed a checklist item</Typography>;

            case 'checklist_item_uncompleted':
                return <Typography variant="small">reopened a checklist item</Typography>;

            case 'timer_started':
                return <Typography variant="small">started a timer</Typography>;

            case 'timer_stopped':
                if (changes && changes.duration_formatted) {
                    return (
                        <Typography variant="small">
                            stopped the timer after <strong>{changes.duration_formatted}</strong>
                        </Typography>
                    );
                }
                return <Typography variant="small">stopped the timer</Typography>;

            case 'manual_time_added':
                if (changes && changes.duration_seconds) {
                    return (
                        <Typography variant="small">
                            added manual time for <strong>{Math.round((changes.duration_seconds / 60) * 10) / 10} minutes</strong>
                        </Typography>
                    );
                }
                return <Typography variant="small">added manual time</Typography>;

            case 'time_entry_updated':
                return <Typography variant="small">updated a time entry</Typography>;

            case 'time_entry_deleted':
                return <Typography variant="small">deleted a time entry</Typography>;

            case 'checklist_item_deleted':
                return <Typography variant="small">deleted a checklist item</Typography>;

            case 'time_logged':
                if (changes && changes.duration) {
                    return (
                        <Typography variant="small">
                            logged <strong>{changes.duration}</strong> of work
                        </Typography>
                    );
                }
                return <Typography variant="small">logged time</Typography>;

            default:
                return <Typography variant="small">{activity.description || `performed ${formatActivityLabel(activityType)}`}</Typography>;
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <Spinner className="h-8 w-8" color="blue" />
            </div>
        );
    }

    if (activities.length === 0) {
        return (
            <div className="text-center py-8">
                <Typography color="gray">No activity yet</Typography>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="relative">
                {/* Timeline Line */}
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />

                {/* Activity Items */}
                <div className="space-y-6">
                    {activities.map((activity, index) => (
                        <div key={activity.log_id} className="relative flex gap-4">
                            {/* Icon */}
                            <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full bg-${getActivityColor(activity.activity_type)}-100 text-${getActivityColor(activity.activity_type)}-600`}>
                                {getActivityIcon(activity.activity_type)}
                            </div>

                            {/* Content */}
                            <Card className="flex-1">
                                <CardBody className="p-4">
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <Typography variant="small" className="font-semibold">
                                                {activity.user_name || activity.user_username || activity.user_id || 'System'}
                                            </Typography>
                                            <Chip
                                                size="sm"
                                                value={formatActivityLabel(activity.activity_type || activity.action_type)}
                                                color={getActivityColor(activity.activity_type || activity.action_type)}
                                                className="capitalize"
                                            />
                                        </div>
                                        <Typography variant="small" color="gray">
                                            {formatDate(activity.created_at)}
                                        </Typography>
                                    </div>

                                    <div className="text-gray-700">
                                        {renderActivityDescription(activity)}
                                    </div>

                                    {activity.metadata && (
                                        <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
                                            <pre className="whitespace-pre-wrap">
                                                {JSON.stringify(JSON.parse(activity.metadata), null, 2)}
                                            </pre>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TaskActivitySection;
